import { useEffect, useId, useMemo, useState } from 'react';
import {
    Navigate,
    generatePath,
    useLocation,
    useNavigate,
    useParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    BicycleIcon,
    BusIcon,
    CarIcon,
    MotorcycleIcon,
    TruckIcon,
} from '@phosphor-icons/react';
import {
    DirectoryItemPage,
    DirectoryItemEventsTab,
    VehicleOverviewTab,
} from '@/features/DirectoryItemPage';
import type { TabItem } from '@/components/Tabs';
import {
    DirectoryItemEventFeed,
    useVehicleOccurrences,
} from '@/features/EventFeed';
import { Badge } from '@/components';
import { toast } from '@/components';
import { AppRoutes } from '@/constants/routes';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { revalidateByCacheKeyPrefix } from '@/utils/swr';
import { isNotFoundError } from '@/utils/http';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { capitalize } from 'lodash';
import { VehicleType } from '@/types/vehicle';
import {
    useDirectoryItemOccurrenceSelection,
    useDocumentTitle,
    useFeedFocus,
} from '@/hooks';
import { VehiclePageSkeleton } from './VehiclePageSkeleton';
import { useVehicleData } from './VehiclePage/hooks';
import { useVehicleIdentityActions } from '@/features/IdentityActions';
import { useEventFeedStore } from '@/stores';
import type { VehicleOccurrence } from '@/types/vehicle';

const VEHICLE_TYPE_ICONS = {
    [VehicleType.CAR]: CarIcon,
    [VehicleType.TRUCK]: TruckIcon,
    [VehicleType.BUS]: BusIcon,
    [VehicleType.MOTORCYCLE]: MotorcycleIcon,
    [VehicleType.BICYCLE]: BicycleIcon,
};

const VEHICLE_TABS = {
    EVENTS: 'events',
    OVERVIEW: 'overview',
} as const;

type VehicleTab = (typeof VEHICLE_TABS)[keyof typeof VEHICLE_TABS];

export default function VehiclePage() {
    const { t } = useTranslation(I18nNamespace.DIRECTORY);
    const { vehicleId } = useParams<{ vehicleId: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const pageInstanceId = useId();
    const returnUrl =
        (location.state as { returnUrl?: string })?.returnUrl ||
        AppRoutes.VEHICLES;
    const [activeTab, setActiveTab] = useState<VehicleTab>(
        VEHICLE_TABS.OVERVIEW,
    );
    const setFeedInteraction = useEventFeedStore(
        (state) => state.setFeedInteraction,
    );

    const {
        focusedItem: selectedOccurrence,
        setFocusedItem: setSelectedOccurrence,
    } = useFeedFocus<VehicleOccurrence>({
        entityId: vehicleId ?? '',
        enabled: activeTab === VEHICLE_TABS.EVENTS,
        getId: (occurrence) => occurrence.event.id,
        setInteraction: setFeedInteraction,
    });

    const {
        vehicle,
        error: vehicleError,
        isLoading: vehicleLoading,
        mutate: mutateVehicle,
    } = useVehicleData({ vehicleId });

    const {
        data: occurrences,
        isLoading: occurrencesLoading,
        isLoadingMore: occurrencesLoadingMore,
        hasMore: hasMoreOccurrences,
        loadMore: loadMoreOccurrences,
        mutate: mutateOccurrences,
    } = useVehicleOccurrences({ vehicleId, pageSize: 20 });

    const { actions: identityActions, modals: identityActionModals } =
        useVehicleIdentityActions({
            vehicle,
            includeDelete: true,
            mutateDetail: mutateVehicle,
            onChanged: () => {},
            onDeleted: () => navigate(returnUrl, { replace: true }),
            onMerged: (targetVehicleId: string) =>
                navigate(
                    generatePath(AppRoutes.VEHICLE, {
                        vehicleId: targetVehicleId,
                    }),
                ),
            onSourceStale: () => navigate(returnUrl, { replace: true }),
        });

    useDocumentTitle('vehicle', vehicle?.name || vehicle?.licensePlate);

    const selectedEventId = selectedOccurrence?.event.id ?? null;

    useEffect(() => {
        setActiveTab(VEHICLE_TABS.OVERVIEW);
    }, [vehicleId]);

    const { handleOccurrenceSelect } = useDirectoryItemOccurrenceSelection({
        occurrences,
        selectedOccurrence,
        setSelectedOccurrence,
        isLoading: occurrencesLoading,
        showEvents: () => setActiveTab(VEHICLE_TABS.EVENTS),
        hasMore: hasMoreOccurrences,
        loadMore: loadMoreOccurrences,
    });

    useEffect(() => {
        if (vehicleError && !isNotFoundError(vehicleError)) {
            toast.error(t('vehicles.failedToLoad'));
        }
    }, [vehicleError, t]);

    const tabs: TabItem[] = useMemo(
        () => [
            {
                id: VEHICLE_TABS.OVERVIEW,
                label: t('vehiclePage.tabs.overview'),
            },
            {
                id: VEHICLE_TABS.EVENTS,
                label: t('vehiclePage.tabs.events'),
            },
        ],
        [t],
    );

    if (
        !vehicleId ||
        (vehicleError && !vehicleLoading) ||
        (!vehicle && !vehicleLoading)
    ) {
        return <Navigate to={returnUrl} replace />;
    }

    if (!vehicle) {
        return <VehiclePageSkeleton />;
    }

    const VehicleIcon = VEHICLE_TYPE_ICONS[vehicle.vehicleType] ?? CarIcon;

    const displayName = vehicle.name
        ? vehicle.name
        : `${t('vehicles.card.unknownVehicle')} ${capitalize(vehicle.vehicleType)}`;

    const renderTabContent = () => {
        switch (activeTab) {
            case VEHICLE_TABS.EVENTS:
                return (
                    <DirectoryItemEventsTab
                        occurrence={selectedOccurrence}
                        isOccurrencesLoading={occurrencesLoading}
                        hasOccurrences={(occurrences ?? []).length > 0}
                    />
                );
            case VEHICLE_TABS.OVERVIEW:
                return <VehicleOverviewTab vehicle={vehicle} />;
            default:
                return null;
        }
    };

    return (
        <>
            <DirectoryItemPage
                headerIcon={
                    <VehicleIcon className="text-grey-300" weight="regular" />
                }
                headerImageUrl={vehicle.thumbnailUrl}
                title={displayName}
                badge={
                    <Badge
                        variant="license-plate"
                        value={vehicle.licensePlate}
                    />
                }
                tags={vehicle.tags}
                actions={identityActions}
                content={{
                    kind: 'tabs',
                    tabs,
                    activeTab,
                    onTabChange: (tabId) => setActiveTab(tabId as VehicleTab),
                    tabContent: renderTabContent(),
                }}
                eventFeed={
                    <DirectoryItemEventFeed
                        key={pageInstanceId}
                        occurrences={occurrences}
                        isLoading={occurrencesLoading}
                        title={t('vehiclePage.vehicleEvents')}
                        viewAllUrl={`${AppRoutes.EVENTS}?vehicleId=${vehicleId}`}
                        navigationFrom="vehicle-events"
                        feedContext={{ type: 'vehicle', vehicleId }}
                        playingEventId={
                            activeTab === VEHICLE_TABS.EVENTS
                                ? selectedEventId
                                : null
                        }
                        onOccurrenceSelect={handleOccurrenceSelect}
                        linkSource={{ kind: 'vehicle', id: vehicleId }}
                        onEntityLinked={() => {
                            void mutateOccurrences();
                            void revalidateByCacheKeyPrefix(
                                CACHE_KEYS.ALL_VEHICLES,
                            );
                        }}
                        onEntityUnlinked={(eventId) => {
                            if (selectedOccurrence?.event.id === eventId) {
                                setSelectedOccurrence(null);
                            }
                            void mutateOccurrences(
                                (pages) =>
                                    pages?.map((page) => ({
                                        ...page,
                                        items: page.items.filter(
                                            (item) => item.event.id !== eventId,
                                        ),
                                    })),
                                { revalidate: false },
                            );
                            void revalidateByCacheKeyPrefix(
                                CACHE_KEYS.ALL_VEHICLES,
                            );
                        }}
                        pagination={{
                            hasMore: hasMoreOccurrences,
                            isLoadingMore: occurrencesLoadingMore,
                            onLoadMore: loadMoreOccurrences,
                        }}
                    />
                }
            />
            {identityActionModals}
        </>
    );
}
